import { del, get, post, put } from '../request';
import { toPage, type Page, type PageQuery } from './types';

export interface AgentProfile {
  id: number;
  name?: string;
  username?: string;
  extension?: string;
  sip_password?: string; // 列表接口已脱敏（"******"）；仅 /agents/me 返回真实值
  state?: string; // idle|break|offline|busy|acw...
  break_reason_id?: number; // 小休原因（仅 state=break 非空）
  break_reason?: string;
  skill_group_id?: number | null;
  org_id?: number | null; // 所属组织（sys_org.id；分公司主管数据分权）
  user_id?: number | null;
  /** 接听设备：softphone=浏览器软电话（默认）；desk=外部 SIP 话机（工作台不注册软电话） */
  phone_mode?: PhoneMode;
  /** 在线在服数/容量上限（agent:state 实时；顶栏渠道开关"在线已满"警示用，v2.1） */
  chat_active?: number;
  chat_max?: number;
  created_at?: string;
  [k: string]: any;
}

/** 接听设备：softphone=浏览器软电话（默认）；desk=外部 SIP 话机 */
export type PhoneMode = 'softphone' | 'desk';

/** 坐席表单（新建/编辑）：不含分机注册密码——全系统统一用「SIP 设置」的全局注册密码，后端保存时自动填 */
export interface AgentForm {
  extension: string;
  skill_group_id?: number;
  user_id: number;
  phone_mode?: PhoneMode;
}

/**
 * GET /agents/me —— 当前登录用户的坐席档案（含 extension/sip_password/state）。
 * 非坐席账号可能 404，调用方静默处理。
 */
export function getMyAgent(): Promise<AgentProfile> {
  return get('/agents/me', undefined, { silent: true });
}

/** 坐席列表查询条件（GET /agents） */
export interface AgentQuery extends PageQuery {
  /** 按所属组织筛选，逗号分隔的多值（组织树筛选取「选中节点 + 全部下级」） */
  org_id?: string;
  /** 按绑定用户精确过滤 */
  user_id?: number;
  /** 技能组精确过滤（未分组坐席 skill_group_id=0，不属于任何组） */
  skill_group_id?: number;
  /** 姓名/分机模糊搜索：坐席量级大时下拉选人的服务端搜索入口 */
  keyword?: string;
}

/** GET /agents（坐席下拉等展示用途；sip_password 为脱敏掩码，不要编辑提交） */
export async function listAgents(q?: AgentQuery): Promise<Page<AgentProfile>> {
  return toPage<AgentProfile>(await get('/agents', { page: 1, pageSize: 100, ...(q || {}) }));
}

/** GET /agents/:id（单条详情，供下拉回显只有 id 的存量值） */
export async function getAgent(id: number): Promise<AgentProfile> {
  return get(`/agents/${id}`, undefined, { silent: true });
}

/** 后端单页上限：httpx.PageParams 对 pageSize > 200 的请求一律回落为 20 */
const AGENT_PAGE_MAX = 200;
/** 循环拉全量的页数上限：名册异常时不无限打后端（50 页 ≈ 1 万坐席） */
const AGENT_ALL_MAX_PAGES = 50;

/**
 * 按条件拉取坐席**全量**（循环分页）。
 * 只给「必须拿到完整名册才能算对」的页面用（如排班月历要覆盖全部在册坐席）；
 * 下拉选人一律走 AgentSelect 的服务端搜索，不要用本函数把整张表拉进内存。
 */
export async function listAllAgents(q?: Omit<AgentQuery, 'page' | 'pageSize'>): Promise<AgentProfile[]> {
  const out: AgentProfile[] = [];
  for (let page = 1; page <= AGENT_ALL_MAX_PAGES; page++) {
    const { list, total } = await listAgents({ ...(q || {}), page, pageSize: AGENT_PAGE_MAX });
    out.push(...list);
    if (list.length === 0 || out.length >= total) break;
  }
  return out;
}

/** 提交负载：分机注册密码不由前端提交（后端按全局注册密码自动对齐） */
function toAgentPayload(f: Partial<AgentForm>): Record<string, any> {
  const p: Record<string, any> = {};
  if (f.extension !== undefined) p.extension = f.extension;
  if (Object.prototype.hasOwnProperty.call(f, 'skill_group_id')) p.skill_group_id = f.skill_group_id ?? null;
  if (f.user_id !== undefined) p.user_id = f.user_id;
  if (f.phone_mode !== undefined) p.phone_mode = f.phone_mode;
  return p;
}

/**
 * 坐席新增/修改/删除的响应：`sync_warning` 非空表示**本次操作已经生效**，只是 FreeSWITCH 目录
 * 同步失败（分机漏登记/漏注销）。这类情况后端按成功返回而不是报错，页面据此提示
 * 「已生效，但目录同步失败，可手动重新同步」，不要把它当失败处理。
 */
export interface AgentSyncResult {
  id?: number;
  sync_warning?: string;
}

export function createAgent(f: AgentForm) {
  return post<AgentSyncResult>('/agents', toAgentPayload(f));
}

export function updateAgent(id: number, f: Partial<AgentForm>) {
  return put<AgentSyncResult>(`/agents/${id}`, toAgentPayload(f));
}

export function deleteAgent(id: number) {
  return del<AgentSyncResult>(`/agents/${id}`);
}

/** POST /agents/me/heartbeat：续租 + 返回档案与状态 */
export function heartbeatAgent(): Promise<AgentProfile> {
  return post('/agents/me/heartbeat', undefined, { silent: true });
}

/** POST /agents/:id/state —— state ∈ idle|busy|break|offline；break 必填 reason_id */
export function setAgentState(id: number, state: string, reasonId?: number) {
  return post(`/agents/${id}/state`, { state, reason_id: reasonId || 0 });
}

// ---------- 坐席基础设置（默认上线状态 / 小休原因） ----------

/** 坐席基础设置（GET /agents/break-config；含 default_online_state=idle|busy） */
export function getBreakConfig(): Promise<{ id?: number; default_online_state?: string }> {
  return get('/agents/break-config');
}

/** 更新坐席基础设置（PUT /agents/break-config；admin） */
export function updateBreakConfig(v: { default_online_state: string }) {
  return put('/agents/break-config', v);
}

/** 小休原因（GET /agents/break-reasons；含 enabled 供坐席端过滤） */
export interface BreakReason {
  id: number;
  name: string;
  sort?: number;
  enabled?: boolean;
  created_at?: string;
  updated_at?: string;
}

export function listBreakReasons(): Promise<BreakReason[]> {
  return get('/agents/break-reasons');
}

export function createBreakReason(v: { name: string; sort?: number }) {
  return post('/agents/break-reasons', v);
}

export function updateBreakReason(id: number, v: Partial<Pick<BreakReason, 'name' | 'sort' | 'enabled'>>) {
  return put(`/agents/break-reasons/${id}`, v);
}

export function deleteBreakReason(id: number) {
  return del(`/agents/break-reasons/${id}`);
}

/** 坐席实时状态视图（GET /agents/states，管理员/班组长驾驶舱用） */
export interface AgentState {
  agent_id: number;
  name?: string;
  extension?: string;
  skill_group_id?: number;
  state?: string;
  updated_at?: number;
  break_reason_id?: number;
  break_reason?: string;
  voice_active?: number;
  chat_active?: number;
  voice_max?: number;
  chat_max?: number;
}

/** GET /agents/states —— 全量坐席实时状态（管理员/班组长仪表盘） */
export async function listAgentStates(): Promise<AgentState[]> {
  const res = await get('/agents/states', undefined, { silent: true });
  return Array.isArray(res) ? (res as AgentState[]) : [];
}
